/**
 * The text chat of one Firmio Meet meeting, from inside the room.
 *
 * Two feeds into one list, deduplicated by id:
 *   - LiveKit's data channel (topic "chat"): a sender posts the line to the
 *     server and then broadcasts the stored line to the room, so it lands on
 *     everyone's screen the moment it exists, with the server's id and time;
 *   - the catch-up poll (`GET /session/chat?since=`): what you missed while
 *     reconnecting, what was written before you were admitted, and a safety
 *     net when a packet never arrived. Slow, because the data channel does
 *     the real-time work.
 *
 * Also counts the lines that arrived while the chat panel was closed, for the
 * badge on the chat button; opening the panel clears it.
 *
 * Shared with the mobile app, which passes its own `getRoom` — hence no DOM
 * and nothing imported beyond the SDK and the API client.
 */
import { RoomEvent, type Participant, type Room } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMeetChatSince, sendMeetChatMessage, type MeetChatMessage } from '../lib/meetApi';

/** Data-channel topic a stored chat line is broadcast on. */
export const MEET_CHAT_TOPIC = 'chat';
const POLL_MS = 6_000;

function mergeLines(current: MeetChatMessage[], incoming: MeetChatMessage[]): MeetChatMessage[] {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((m) => m.id));
  const fresh = incoming.filter((m) => !seen.has(m.id));
  if (fresh.length === 0) return current;
  return [...current, ...fresh].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function useMeetChat(input: {
  /** The participant token the join handed back; null before joining. */
  token: string | null;
  /** True while in the room; the hook is idle otherwise. */
  active: boolean;
  /** True while the chat panel is open: new lines are read, not counted. */
  panelOpen: boolean;
  /** The live SDK room, for the data channel. Null when not connected. */
  getRoom: () => Room | null;
  /** Re-run the data-channel subscription when the connection (re)appears. */
  roomStatus: string;
}): {
  messages: MeetChatMessage[];
  unread: number;
  sending: boolean;
  error: 'meet.chat.sendFailed' | null;
  send: (text: string) => Promise<boolean>;
} {
  const { token, active, panelOpen, getRoom, roomStatus } = input;
  const [messages, setMessages] = useState<MeetChatMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<'meet.chat.sendFailed' | null>(null);
  // Own lines never count as unread; the panel being open neither.
  const panelOpenRef = useRef(panelOpen);
  panelOpenRef.current = panelOpen;
  // The list is also kept in a ref so two feeds landing in the same tick
  // (a packet and a poll) each merge into the latest list, not a stale one.
  const messagesRef = useRef<MeetChatMessage[]>([]);

  const absorb = useCallback((incoming: MeetChatMessage[], mine: boolean) => {
    const current = messagesRef.current;
    const next = mergeLines(current, incoming);
    if (next === current) return;
    messagesRef.current = next;
    setMessages(next);
    if (!mine && !panelOpenRef.current) setUnread((n) => n + (next.length - current.length));
  }, []);

  useEffect(() => {
    if (panelOpen) setUnread(0);
  }, [panelOpen, messages.length]);

  // A new meeting (new token): start from nothing.
  useEffect(() => {
    messagesRef.current = [];
    setMessages([]);
    setUnread(0);
    setError(null);
  }, [token]);

  // --- the catch-up poll ------------------------------------------------------
  useEffect(() => {
    if (!active || !token) return;
    let cancelled = false;
    let since: string | null = null;
    const tick = async () => {
      try {
        const res = await fetchMeetChatSince(token, since);
        if (cancelled) return;
        if (res.messages.length > 0) {
          since = res.messages[res.messages.length - 1].createdAt;
          // Our own lines are already in the list from `send`, so the merge
          // drops them and they never count as unread.
          absorb(res.messages, false);
        }
      } catch {
        // Retried on the next tick.
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [absorb, active, token]);

  // --- the data channel ---------------------------------------------------------
  useEffect(() => {
    if (!active) return;
    const room = getRoom();
    if (!room) return;
    const onData = (
      payload: Uint8Array,
      _participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== MEET_CHAT_TOPIC) return;
      try {
        const line = JSON.parse(new TextDecoder().decode(payload)) as MeetChatMessage;
        if (line && typeof line.id === 'string' && typeof line.text === 'string') {
          absorb([line], false);
        }
      } catch {
        // not ours
      }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [absorb, active, getRoom, roomStatus]);

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!token || !trimmed) return false;
      setSending(true);
      setError(null);
      try {
        const line = await sendMeetChatMessage(token, trimmed);
        absorb([line], true);
        const room = getRoom();
        if (room) {
          void room.localParticipant
            .publishData(new TextEncoder().encode(JSON.stringify(line)), {
              reliable: true,
              topic: MEET_CHAT_TOPIC,
            })
            .catch(() => undefined);
        }
        return true;
      } catch {
        setError('meet.chat.sendFailed');
        return false;
      } finally {
        setSending(false);
      }
    },
    [absorb, getRoom, token],
  );

  return { messages, unread, sending, error, send };
}
